import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Departures } from 'app/core/enums/common.enum';
import { FinanceResources } from 'app/core/security/resources/resources';
import { LocationService } from 'app/modules/sho_management_pro/locations/services/location.service';
import { PlaydateService } from 'app/modules/sho_management_pro/play-dates/services/playdate.service';
import { VenueFinancesService } from 'app/modules/sho_management_pro/venue/services/venue-finances.service';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AdapterService } from '../../services/adapters/adapter.service';
import { ConcessionNoveltiesSalesService } from '../../services/sales/concession-novelties/concession-novelties-sales.service';
import { TotalGrossService } from '../../services/sales/total-gross.service';
import * as NoveltiesSaleActions from './novelties-sales.actions';

@Injectable()
export class NoveltiesSalesEffects {
    private _actions$ = inject(Actions);
    private _saleService = inject(ConcessionNoveltiesSalesService);
    private _alertService = inject(AlertService);
    private adapter = inject(AdapterService);
    private totalGrossService = inject(TotalGrossService);
    private _venueFinancesService = inject(VenueFinancesService);
    private _playdatesService = inject(PlaydateService);
    private _locationService = inject(LocationService);

    messages = {
        getAllError: 'Error getting all Novelties Sales',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Novelties Sales
    getAllNoveltiesSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NoveltiesSaleActions.loadNoveltiesSales),
            switchMap((action) => {
                return this._saleService
                    .getAll(action.playdateId, Departures.NOVELTIES)
                    .pipe(
                        map((res) => {
                            const data = this.adapter.adaptCncNvlSalesList(
                                res.data,
                                FinanceResources.NOVELTIES_ITEM
                            );
                            return NoveltiesSaleActions.loadNoveltiesSalesSuccess(
                                {
                                    data: data,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                NoveltiesSaleActions.loadNoveltiesSalesFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get One Novelties Sale
    // getNoveltiesSale$ = createEffect(() =>
    //     this._actions$.pipe(
    //         ofType(NoveltiesSaleActions.loadNoveltiesSale),
    //         switchMap((action) => {
    //             return this._saleService.getOne(action.id).pipe(
    //                 map((res: any) => {
    //                     return NoveltiesSaleActions.loadNoveltiesSaleSuccess({
    //                         data: res.data,
    //                     });
    //                 }),
    //                 catchError((error) =>
    //                     of(
    //                         NoveltiesSaleActions.loadNoveltiesSaleFailure({
    //                             error:
    //                                 error?.error?.message ||
    //                                 this.messages.unknownError,
    //                         })
    //                     )
    //                 )
    //             );
    //         })
    //     )
    // );

    // Create Novelties Sale
    createNoveltiesSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NoveltiesSaleActions.createNoveltiesSale),
            switchMap((action) => {
                return this._saleService.create(action.data).pipe(
                    // tap((res) => console.log(res)), // Debug
                    map((res) => {
                        const sale = this.adapter.adaptCncNvlSale(
                            res.data,
                            FinanceResources.NOVELTIES_ITEM
                        );
                        return NoveltiesSaleActions.createNoveltiesSaleSuccess({
                            sale: sale,
                            success: 'Novelties Sale created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            NoveltiesSaleActions.createNoveltiesSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Novelties Sale
    updateNoveltiesSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NoveltiesSaleActions.updateNoveltiesSale),
            switchMap((action) => {
                return this._saleService.update(action.data, action.id).pipe(
                    // tap((res) => console.log(res)), // Debug
                    map((res) => {
                        const sale = this.adapter.adaptCncNvlSale(
                            res.data,
                            FinanceResources.NOVELTIES_ITEM
                        );
                        return NoveltiesSaleActions.updateNoveltiesSaleSuccess({
                            data: sale,
                            success: 'Novelties Sale updated successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            NoveltiesSaleActions.updateNoveltiesSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Sale
    deleteNoveltiesSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NoveltiesSaleActions.deleteNoveltiesSale),
            switchMap((action) => {
                return this._saleService.delete(action.payload).pipe(
                    // tap((res) => console.log(res)), // Debug
                    map((res) => {
                        return NoveltiesSaleActions.deleteNoveltiesSaleSuccess({
                            deletedSales: res.data.deleted_sales,
                            remainingSales: res.data.remaining_sales,
                            success: 'Novelties Sale deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            NoveltiesSaleActions.deleteNoveltiesSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Sale Item
    deleteNoveltiesSaleItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NoveltiesSaleActions.deleteNoveltiesSaleItem),
            switchMap((action) => {
                return this._saleService.delete(action.payload).pipe(
                    // tap((res) => console.log(res)), // Debug
                    map((res) => {
                        const saleId = Number(res.data.deleted_sales[0]);
                        const totals = res.data.totals;

                        if (!saleId) {
                            const message =
                                'Sale item ID not found in response';
                            this._alertService.showAlert(message, 'error');
                            throw new Error(message);
                        }

                        return NoveltiesSaleActions.deleteNoveltiesSaleItemSuccess(
                            {
                                deletedSaleItem: saleId,
                                totals,
                                success:
                                    'Novelties sale item deleted successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            NoveltiesSaleActions.deleteNoveltiesSaleItemFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onNoveltiesSaleActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    NoveltiesSaleActions.createNoveltiesSaleSuccess,
                    NoveltiesSaleActions.updateNoveltiesSaleSuccess,
                    NoveltiesSaleActions.deleteNoveltiesSaleSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');

                    // Get playdate id
                    const playdateId =
                        this._playdatesService.getLatestPlaydateId();

                    this._locationService.checkCommissionsLocations(
                        playdateId,
                        Departures.NOVELTIES
                    );

                    // Recalculate commissions
                    // this._commonSalesService
                    //     .addCommission(
                    //         this._playdatesService.getLatestPlaydateId(),
                    //         Departures.NOVELTIES
                    //     )
                    //     .subscribe(() => {
                    // Reload totals
                    this._venueFinancesService.onUpdateFinances = true;
                    this.totalGrossService.onValidateCnNvFinances(
                        Departures.NOVELTIES
                    );
                    // });
                })
            ),
        { dispatch: false }
    );

    onNoveltiesSaleActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    NoveltiesSaleActions.loadNoveltiesSalesFailure,
                    NoveltiesSaleActions.loadNoveltiesSaleFailure,
                    NoveltiesSaleActions.createNoveltiesSaleFailure,
                    NoveltiesSaleActions.updateNoveltiesSaleFailure,
                    NoveltiesSaleActions.deleteNoveltiesSaleFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
